<script setup lang="ts">
import { type NotificationType, useToasterMessages } from '@/composables/game/toasters'
import { type Component, ref, watch } from 'vue'
import ErrorCircleIcon from '@/components/icons/ErrorCircleIcon.vue'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'
import { twMerge } from 'tailwind-merge'
import InfoCircleIcon from '@/components/icons/InfoCircleIcon.vue'
import CheckCirclePinkBorder from '@/components/icons/CheckCirclePinkBorder.vue'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import SpinnerIcon from '@/components/icons/SpinnerIcon.vue'

const { messages } = useToasterMessages()

const show = ref<boolean>(true)
const { topStyle } = useFullScreenDimensions()

watch(messages, () => {
  show.value = true
}, { deep: true })

const iconMap: Record<NotificationType, Component | undefined> = {
  error: ErrorCircleIcon,
  success: CheckCirclePinkBorder,
  warn: InfoCircleIcon,
}

const textClass: Record<NotificationType, string> = {
  error: 'text-red-danger',
  success: 'text-white',
  warn: 'text-orange-gold',
}

</script>
<template>
  <Teleport
    to="#app"
  >
    <div
      class="flex items-center justify-center w-full"
    >
      <div
        v-if="show"
        class="fixed w-full max-w-[440px] mx-auto px-4 z-[1000001]"
        :style="topStyle"
      >
        <transition-group
          name="slide"
          :css="true"
        >
          <div
            v-for="(message, index) in messages"
            :key="index"
            class="toaster h-[44px] max-h-[44px] bg-dark-bottom-layer outline outline-1 outline-offset-[-1px] outline-white/[.12] py-1 mb-1 px-3 rounded-[35px] w-full flex justify-between items-center gap-x-3 min-w-0"
          >
            <SpinnerIcon
              v-if="message.loading"
              :class="twMerge('loading-spinner h-6 w-6 min-w-[24px]', textClass[message.type ?? 'success'])"
            />
            <component
              v-else-if="message.icon"
              :is="message.icon"
              class="size-6"
            />
            <component
              v-else-if="message.type"
              :is="iconMap[message.type]"
              class="size-6"
            />
            <div class="flex flex-col w-full flex-1">
              <div
                :class="twMerge('text-base font-semibold tracking-tight', textClass[message.type ?? 'success'], message.description && 'text-sm -tracking-[0.28px]')"
                v-html="message.title"
              />
              <template v-if="message.description">
                <div
                  class="text-xs font-medium text-white/60 -tracking-[0.24px]"
                  v-if="typeof message.description === 'string'"
                  v-html="message.description"
                />
                <component
                  v-else
                  :is="message.description"
                  class="text-xs font-medium text-white/60 -tracking-[0.24px]"
                />
              </template>
            </div>
            <CrossCircleIcon
              class="size-6"
              @click="show = false"
            />
          </div>
        </transition-group>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss">

.toaster {
	box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.40);
}

.slide-enter-active,
.slide-leave-active {
	transition: transform 1s cubic-bezier(0.175, 0.885, 0.32, 1.175), opacity 1s cubic-bezier(0.175, 0.885, 0.32, 1.175);
}

.slide-enter-from {
	transform: translateY(-80px);
	opacity: 0;
}

.slide-enter-to {
	transform: translateY(0);
	opacity: 1;
}

.slide-leave-from {
	transform: translateY(0);
	opacity: 1;
}

.slide-leave-to {
	transform: translateY(-80px);
	opacity: 0;
}
</style>
